import type { RequestInfo } from '@warp-drive/core/types/request';
import { buildBaseURL, type QueryUrlOptions } from '@warp-drive/utilities';
import { SkipCache } from '@warp-drive/core-types/request';
import { camelizeAttributes } from '../utils/camelize-attributes.ts';

const companiesUrl = (path?: string) => {
  const opts: QueryUrlOptions = {
    identifier: { type: 'companies' },
    op: 'query',
    resourcePath: 'companies',
  };
  const base = buildBaseURL(opts);
  return path ? `${base}/${path.replace(/^\/+/, '')}` : base;
};

/**
 * GET /companies/:id/tas-approvers — returns a JSON:API document of
 * Employee records eligible to approve TAS for the company. Callers
 * should `store.push(response.content)` themselves to materialize the
 * employees (mirrors the legacy `pushPayload` + `push` calls inside the
 * old CompanyAdapter#getTasApprovers).
 */
export function getTasApproversRequest(companyId: string): RequestInfo {
  return {
    url: companiesUrl(`${companyId}/tas-approvers`),
    method: 'GET',
    cacheOptions: { [SkipCache]: true },
  };
}

/**
 * GET /companies/:id/account-managers — returns a JSON:API document of the
 * TIO Employee records holding the ACCOUNT_MANAGER relationship for the
 * company. Read the raw document via `parseAccountManagers(content)` — the
 * payload's resource `type` is `employees` (plural), which does NOT bind to
 * the singular `employee` schema, so `store.push` yields records with empty
 * attributes. This parser reads `data[].attributes` directly instead.
 */
export function getAccountManagersRequest(companyId: string): RequestInfo {
  return {
    url: companiesUrl(`${companyId}/account-managers`),
    method: 'GET',
    cacheOptions: { [SkipCache]: true },
  };
}

export interface AccountManager {
  id: string;
  fullName: string;
  email: string;
}

interface AccountManagersDocument {
  data?: { id: string; attributes?: Record<string, unknown> }[];
}

/**
 * Parse the getAccountManagersRequest response into plain AccountManager
 * objects. camelizeAttributes tolerates either key format the request layer
 * negotiates (`first-name` / `firstName`).
 */
export function parseAccountManagers(content: AccountManagersDocument): AccountManager[] {
  return (content.data ?? []).map((resource) => {
    const attrs = camelizeAttributes(resource.attributes);
    const fullName = [attrs['firstName'], attrs['lastName']]
      .filter((part): part is string => typeof part === 'string' && part.length > 0)
      .join(' ');
    return {
      id: resource.id,
      fullName,
      email: typeof attrs['email'] === 'string' ? attrs['email'] : '',
    };
  });
}

/** GET /companies/:id/eligibility-employee-counts — admin dashboard widget. */
export function getEligibilityCountsRequest(companyId: string): RequestInfo {
  return {
    url: companiesUrl(`${companyId}/eligibility-employee-counts`),
    method: 'GET',
    cacheOptions: { [SkipCache]: true },
  };
}

/** GET /companies/:id/participation-employee-counts — admin dashboard widget. */
export function getParticipationCountsRequest(companyId: string): RequestInfo {
  return {
    url: companiesUrl(`${companyId}/participation-employee-counts`),
    method: 'GET',
    cacheOptions: { [SkipCache]: true },
  };
}
